import {
  CONVERSIONS,
  DEFAULT_LANG,
  INVITATION,
  LANGS,
  LANG_GLYPH,
  TELESCOPE_CAPTION,
  UI,
} from './text.js';

/**
 * Which language the city is being read in.
 *
 * The city opens in Chinese. A `?lang=` in the address wins over everything,
 * because that is what the QR card hands to a phone; after that, whatever the
 * reader last chose here; and only then the default. The browser's own
 * preference is deliberately *not* consulted — a piece that opens in a
 * different language depending on whose laptop it is cannot be shown to
 * anyone.
 *
 * It is a module-level store rather than something on the context because the
 * words are read in places that never see a context, and because switching
 * must not rebuild the city: nothing below `ui/` ever asks what language it
 * is, so a change costs exactly the re-render of the overlay.
 */

const KEY = 'isidora:lang';

/** @type {Set<(lang: string) => void>} */
const listeners = new Set();

let current = resolveLang();

function resolveLang() {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  const asked = params.get('lang');
  if (asked && LANGS.includes(asked)) return asked;
  try {
    const kept = globalThis.localStorage?.getItem(KEY);
    if (kept && LANGS.includes(kept)) return kept;
  } catch {
    // Private browsing refuses storage; the default is a fine answer.
  }
  return DEFAULT_LANG;
}

export function lang() {
  return current;
}

export function setLang(next) {
  if (!LANGS.includes(next) || next === current) return current;
  current = next;
  try {
    globalThis.localStorage?.setItem(KEY, next);
  } catch {
    // Nothing to do; the choice simply will not outlive the tab.
  }
  if (globalThis.document) globalThis.document.documentElement.lang = htmlLang(next);
  for (const fn of listeners) fn(next);
  return current;
}

/** The next language round the ring — the whole of the language control. */
export function cycleLang() {
  const i = LANGS.indexOf(current);
  return setLang(LANGS[(i + 1) % LANGS.length]);
}

/** @param {(lang: string) => void} fn @returns {() => void} unsubscribe */
export function onLangChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** What belongs in `<html lang>`; `zh` here means Taiwan's written Chinese. */
export function htmlLang(l = current) {
  return l === 'zh' ? 'zh-Hant-TW' : l;
}

/** The label the language glyph wears: the language one press away. */
export function langGlyph() {
  return LANG_GLYPH[current];
}

/** One of the strings in `UI`. */
export function ui(key) {
  return (UI[current] ?? UI[DEFAULT_LANG])[key];
}

/** The line of the passage this fragment is, in the language being read. */
export function lineOf(fragment) {
  return fragment[current] ?? fragment[DEFAULT_LANG];
}

export function invitation() {
  return INVITATION[current] ?? INVITATION[DEFAULT_LANG];
}

export function telescopeCaption() {
  return TELESCOPE_CAPTION[current] ?? TELESCOPE_CAPTION[DEFAULT_LANG];
}

/** The pool of whispers for a kind of thing, or null if it has none. */
export function conversions(kind) {
  const table = CONVERSIONS[current] ?? CONVERSIONS[DEFAULT_LANG];
  return table[kind] ?? null;
}
